import {
  parseFragment,
  serialize,
  type DefaultTreeAdapterTypes,
  type Token,
} from 'parse5';
import { parseCondition } from './condition';
import type { TemplateNode } from './types';

type Token =
  | { kind: 'html'; html: string }
  | { kind: 'variable'; path: string; persist?: boolean; originalHtml?: string }
  | {
      kind: 'function';
      functionName: string;
      variablePath: string;
      format?: string;
    }
  | { kind: 'if-start'; expression: string }
  | { kind: 'if-end'; expression: string }
  | { kind: 'loop-start'; itemName: string; collectionPath: string }
  | { kind: 'loop-end'; expression: string };

/**
 * Reads a named HTML attribute value from a parse5 element.
 * Used throughout tokenization to extract template data attributes
 * (`data-var`, `data-fn`, `data-start-if`, `data-end-if`, `data-start-loop`, `data-end-loop`)
 * and structural attributes like `class`.
 */
function getAttr(
  el: DefaultTreeAdapterTypes.Element,
  name: string
): string | undefined {
  return el.attrs.find(a => a.name === name)?.value;
}

/**
 * Checks whether a parse5 element has a specific CSS class in its `class` attribute.
 * Used to identify the four template marker span types:
 * `template-variable-highlight`, `template-function-highlight`,
 * `template-condition-highlight`, and `template-loop-highlight`.
 */
function hasClass(el: DefaultTreeAdapterTypes.Element, cls: string): boolean {
  const classAttr = getAttr(el, 'class') ?? '';
  return classAttr.split(/\s+/).includes(cls);
}

/**
 * Reconstructs the raw opening HTML tag string (e.g. `<ul class="list">`) for a parse5 element.
 * Used by `tokenizeNode` when it needs to emit an opening tag before descending into children
 * separately, preserving all attributes on structural wrapper elements like
 * `<ul>`, `<tbody>`, `<dl>`, `<tr>`, etc.
 */
function serializeOpenTag(el: DefaultTreeAdapterTypes.Element): string {
  const attrs = el.attrs
    .map(
      (a: Token.Attribute) => ` ${a.name}="${a.value.replace(/"/g, '&quot;')}"`
    )
    .join('');
  return `<${el.nodeName}${attrs}>`;
}

/**
 * Checks whether a `<ul>` or `<ol>` element contains at least one `<li>` child
 * that carries a template token (loop-start, loop-end, variable, etc.) rather than
 * being plain HTML. Determines whether the list needs child-by-child tokenization
 * instead of bulk serialization.
 */
function hasListMarkers(el: DefaultTreeAdapterTypes.Element): boolean {
  return el.childNodes.some(
    (child: DefaultTreeAdapterTypes.ChildNode) =>
      child.nodeName === 'li' && classifyNode(child).kind !== 'html'
  );
}

/**
 * Checks whether a `<tbody>` element contains at least one `<tr>` child that carries
 * a template token. Detects table bodies used as loop containers where rows represent
 * loop-start / loop-end markers, triggering row-by-row tokenization instead of
 * bulk serialization.
 */
function hasTbodyMarkers(el: DefaultTreeAdapterTypes.Element): boolean {
  return el.childNodes.some(
    (child: DefaultTreeAdapterTypes.ChildNode) =>
      child.nodeName === 'tr' && classifyNode(child).kind !== 'html'
  );
}

/**
 * Recursively scans an element's entire subtree looking for any `<span>` that is
 * a template marker (variable, function, condition, or loop). Used to detect
 * block-level elements (e.g. `<p>`, `<td>`, `<h1>`) that indirectly wrap template
 * spans and therefore need to be expanded (open tag + children tokenized separately
 * + close tag) rather than serialized as a single HTML blob.
 */
function hasDeepMarker(el: DefaultTreeAdapterTypes.Element): boolean {
  for (const child of el.childNodes) {
    if (
      child.nodeName === 'span' &&
      classifyNode(child as DefaultTreeAdapterTypes.Element).kind !== 'html'
    )
      return true;
    if (child.nodeName !== '#text' && child.nodeName !== '#comment') {
      if (hasDeepMarker(child as DefaultTreeAdapterTypes.Element)) return true;
    }
  }
  return false;
}

/**
 * Checks whether a `<dl>` element contains at least one `<div>` child that carries
 * a template token. Definition lists may contain template loop markers inside wrapper
 * divs, requiring div-by-div tokenization rather than bulk serialization.
 */
function hasDlMarkers(el: DefaultTreeAdapterTypes.Element): boolean {
  return el.childNodes.some(
    (child: DefaultTreeAdapterTypes.ChildNode) =>
      child.nodeName === 'div' && classifyNode(child).kind !== 'html'
  );
}

/**
 * Core of phase 1 (tokenize). Converts a single parse5 DOM node into one or more
 * `Token` entries appended to `tokens`. Handles special structural cases in priority order:
 * - `ul`/`ol` with list markers → emit open tag, classify each `<li>`, emit close tag
 * - `table` → emit open tag, recurse into children, emit close tag
 * - `tbody` with row markers → emit open tag, classify each `<tr>`, emit close tag
 * - `dl` with div markers → emit open tag, classify each `<div>`, emit close tag
 * - Any element with a deep marker span → emit open tag, recurse children, emit close tag
 * - Everything else → delegate to `classifyNode` for a single token
 */
function tokenizeNode(
  node: DefaultTreeAdapterTypes.ChildNode,
  tokens: Token[]
): void {
  if (
    (node.nodeName === 'ul' || node.nodeName === 'ol') &&
    hasListMarkers(node as DefaultTreeAdapterTypes.Element)
  ) {
    const el = node as DefaultTreeAdapterTypes.Element;
    tokens.push({ kind: 'html', html: `<${node.nodeName}>` });
    for (const child of el.childNodes) {
      if (child.nodeName === 'li') {
        const token = classifyNode(child);
        if (token.kind !== 'html') {
          tokens.push(token);
        } else {
          const liEl = child as DefaultTreeAdapterTypes.Element;
          tokens.push({ kind: 'html', html: serializeOpenTag(liEl) });
          for (const liChild of liEl.childNodes) {
            tokenizeNode(liChild, tokens);
          }
          tokens.push({ kind: 'html', html: '</li>' });
        }
      } else {
        tokenizeNode(child, tokens);
      }
    }
    tokens.push({ kind: 'html', html: `</${node.nodeName}>` });
    return;
  }

  if (node.nodeName === 'table') {
    const el = node as DefaultTreeAdapterTypes.Element;
    tokens.push({ kind: 'html', html: serializeOpenTag(el) });
    for (const child of el.childNodes) {
      tokenizeNode(child, tokens);
    }
    tokens.push({ kind: 'html', html: '</table>' });
    return;
  }

  if (
    node.nodeName === 'tbody' &&
    hasTbodyMarkers(node as DefaultTreeAdapterTypes.Element)
  ) {
    const el = node as DefaultTreeAdapterTypes.Element;
    tokens.push({ kind: 'html', html: '<tbody>' });
    for (const child of el.childNodes) {
      if (child.nodeName === 'tr') {
        const token = classifyNode(child);
        if (token.kind !== 'html') {
          tokens.push(token);
        } else {
          const trEl = child as DefaultTreeAdapterTypes.Element;
          tokens.push({ kind: 'html', html: serializeOpenTag(trEl) });
          for (const trChild of trEl.childNodes) {
            tokenizeNode(trChild, tokens);
          }
          tokens.push({ kind: 'html', html: '</tr>' });
        }
      } else {
        tokenizeNode(child, tokens);
      }
    }
    tokens.push({ kind: 'html', html: '</tbody>' });
    return;
  }

  if (
    node.nodeName === 'dl' &&
    hasDlMarkers(node as DefaultTreeAdapterTypes.Element)
  ) {
    const el = node as DefaultTreeAdapterTypes.Element;
    tokens.push({ kind: 'html', html: '<dl>' });
    for (const child of el.childNodes) {
      if (child.nodeName === 'div') {
        const token = classifyNode(child);
        if (token.kind !== 'html') {
          tokens.push(token);
        } else {
          const divEl = child as DefaultTreeAdapterTypes.Element;
          tokens.push({ kind: 'html', html: serializeOpenTag(divEl) });
          for (const divChild of divEl.childNodes) {
            tokenizeNode(divChild, tokens);
          }
          tokens.push({ kind: 'html', html: '</div>' });
        }
      } else {
        tokenizeNode(child, tokens);
      }
    }
    tokens.push({ kind: 'html', html: '</dl>' });
    return;
  }

  // Expand block elements that contain template marker spans as direct children
  if (node.nodeName !== '#text' && node.nodeName !== '#comment') {
    const el = node as DefaultTreeAdapterTypes.Element;
    const hasMarker = hasDeepMarker(el);
    if (hasMarker) {
      tokens.push({ kind: 'html', html: serializeOpenTag(el) });
      for (const child of el.childNodes) {
        tokenizeNode(child, tokens);
      }
      tokens.push({ kind: 'html', html: `</${el.nodeName}>` });
      return;
    }
  }
  tokens.push(classifyNode(node));
}

/**
 * Phase 1 entry point. Uses parse5 to parse the HTML template string into a DOM
 * fragment, then walks each top-level node through `tokenizeNode` to produce a flat
 * `Token[]` array ready for the recursive phase 2 parser (`parseNodes`).
 */
function tokenize(html: string): Token[] {
  const fragment = parseFragment(html);
  const tokens: Token[] = [];
  for (const node of fragment.childNodes) {
    tokenizeNode(node, tokens);
  }
  return tokens;
}

/**
 * Inspects a single DOM node and returns the matching `Token`. Only `span`, `li`,
 * `tr`, and `div` nodes can be template markers; all other nodes are returned as
 * `{ kind: 'html' }`. For spans, reads CSS classes and data attributes to determine
 * which of the four marker types it is: variable, function, if-start/if-end, or
 * loop-start/loop-end.
 */
function classifyNode(node: DefaultTreeAdapterTypes.ChildNode): Token {
  if (
    node.nodeName !== 'span' &&
    node.nodeName !== 'li' &&
    node.nodeName !== 'tr' &&
    node.nodeName !== 'div'
  ) {
    return {
      kind: 'html',
      html: serialize({
        childNodes: [node],
      } as DefaultTreeAdapterTypes.DocumentFragment),
    };
  }

  const el = node as DefaultTreeAdapterTypes.Element;

  // For tr and div, only classify if they carry a template marker class
  if (node.nodeName === 'tr' || node.nodeName === 'div') {
    if (
      !hasClass(el, 'template-loop-highlight') &&
      !hasClass(el, 'template-condition-highlight')
    ) {
      return {
        kind: 'html',
        html: serialize({ childNodes: [node] } as any),
      };
    }
  }

  if (hasClass(el, 'template-variable-highlight')) {
    const path = getAttr(el, 'data-var');
    if (path !== undefined) {
      const persist = getAttr(el, 'data-persist') === 'true';
      const originalHtml = persist
        ? serialize({ childNodes: [node] } as any)
        : undefined;
      return { kind: 'variable', path, persist, originalHtml };
    }
  }

  if (hasClass(el, 'template-function-highlight')) {
    const dataFn = getAttr(el, 'data-fn');
    if (dataFn !== undefined) {
      const match = dataFn.match(
        /^(uppercase|lowercase|formatDate|formatDateTime)\(((?:@\.)?[^,)]+)(?:,\s*'([^']+)')?\)$/
      );
      if (match) {
        return {
          kind: 'function',
          functionName: match[1],
          variablePath: match[2],
          format: match[3],
        };
      }
    }
  }

  if (hasClass(el, 'template-condition-highlight')) {
    const startIf = getAttr(el, 'data-start-if');
    if (startIf !== undefined) {
      return { kind: 'if-start', expression: startIf };
    }
    const endIf = getAttr(el, 'data-end-if');
    if (endIf !== undefined) {
      return { kind: 'if-end', expression: endIf };
    }
  }

  if (hasClass(el, 'template-loop-highlight')) {
    const startLoop = getAttr(el, 'data-start-loop');
    if (startLoop !== undefined) {
      const colonIdx = startLoop.indexOf(' : ');
      if (colonIdx === -1) {
        throw new Error(
          `Invalid loop syntax: "${startLoop}". Expected "item : collection"`
        );
      }
      const itemName = startLoop.slice(0, colonIdx).trim();
      const collectionPath = startLoop.slice(colonIdx + 3).trim();
      return { kind: 'loop-start', itemName, collectionPath };
    }
    const endLoop = getAttr(el, 'data-end-loop');
    // end-loop marker (with or without data-end-loop attribute)
    return { kind: 'loop-end', expression: endLoop ?? '' };
  }

  return {
    kind: 'html',
    html: serialize({ childNodes: [node] } as any),
  };
}

type TokenStream = { tokens: Token[]; pos: number };

/**
 * Phase 2 recursive descent parser. Consumes tokens from the shared `stream` and
 * builds a `TemplateNode[]` subtree. When it encounters `if-start` or `loop-start`
 * it calls itself recursively with the matching stop sentinel, naturally nesting child
 * nodes. Consecutive `html` tokens are merged into a single `TextNode` to minimize
 * the AST.
 *
 * @param stream - Shared mutable token stream (position advances as tokens are consumed).
 * @param stopAt - Optional sentinel that terminates the current recursion level:
 *   `'if-end'` closes a conditional block, `'loop-end'` closes a loop block.
 */
function parseNodes(
  stream: TokenStream,
  stopAt?: 'if-end' | 'loop-end'
): TemplateNode[] {
  const nodes: TemplateNode[] = [];

  while (stream.pos < stream.tokens.length) {
    const token = stream.tokens[stream.pos];

    if (stopAt === 'if-end' && token.kind === 'if-end') {
      stream.pos++;
      return nodes;
    }
    if (stopAt === 'loop-end' && token.kind === 'loop-end') {
      stream.pos++;
      return nodes;
    }

    stream.pos++;

    if (token.kind === 'html') {
      const last = nodes[nodes.length - 1];
      if (last?.type === 'text') {
        last.html += token.html;
      } else {
        nodes.push({ type: 'text', html: token.html });
      }
    } else if (token.kind === 'variable') {
      nodes.push({
        type: 'variable',
        path: token.path,
        persist: token.persist,
        originalHtml: token.originalHtml,
      });
    } else if (token.kind === 'function') {
      nodes.push({
        type: 'function',
        functionName: token.functionName,
        variablePath: token.variablePath,
        format: token.format,
      });
    } else if (token.kind === 'if-start') {
      const expression = parseCondition(token.expression);
      const children = parseNodes(stream, 'if-end');
      nodes.push({ type: 'if', expression, children });
    } else if (token.kind === 'loop-start') {
      const children = parseNodes(stream, 'loop-end');
      nodes.push({
        type: 'loop',
        itemName: token.itemName,
        collectionPath: token.collectionPath,
        children,
      });
    } else if (token.kind === 'if-end') {
      throw new Error('Unexpected {{/if}} without matching {{#if}}');
    } else if (token.kind === 'loop-end') {
      throw new Error('Unexpected {{/loop}} without matching {{#loop}}');
    }
  }

  if (stopAt) {
    return nodes;
  }

  return nodes;
}

/**
 * Public API — the entry point consumed by `renderTemplate` in `index.ts`.
 * Orchestrates the full two-phase pipeline:
 * 1. `tokenize(html)` — parse5 walks the DOM into a flat `Token[]` array
 * 2. `parseNodes(stream)` — recursive descent converts the token stream into a `TemplateNode[]` AST
 *
 * @param html - Raw HTML template string containing template marker spans.
 * @returns Structured AST ready for evaluation by `evaluate` in `evaluator.ts`.
 */
export function parse(html: string): TemplateNode[] {
  const tokens = tokenize(html);
  const stream: TokenStream = { tokens, pos: 0 };
  return parseNodes(stream, undefined);
}
